/**
 * Fournisseur de thème : expose la préférence d'affichage (« clair / sombre / système »),
 * applique le thème Material UI et reflète le mode sur `<html data-theme>` (convention maquettes).
 */

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ThemePreference } from '@noryvox/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  createAppTheme,
  persistThemePreference,
  readThemePreference,
  resolveThemeMode,
  type ThemeMode,
} from './theme';

export interface ThemeModeContextValue {
  /** Préférence choisie par l'utilisateur. */
  preference: ThemePreference;
  /** Mode effectif appliqué. */
  mode: ThemeMode;
  setPreference: (preference: ThemePreference) => void;
  /** Bascule entre clair et sombre (résout `system` avant de basculer). */
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/** Accède à la préférence d'affichage courante ; doit être imbriqué dans `ThemeModeProvider`. */
export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);
  if (context === null) {
    throw new Error("useThemeMode doit être utilisé à l'intérieur de ThemeModeProvider.");
  }
  return context;
}

export function ThemeModeProvider({ children }: { children: ReactNode }): ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>(readThemePreference);
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const mode = resolveThemeMode(preference, systemPrefersDark);
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    persistThemePreference(next);
  }, []);

  const toggleMode = useCallback(() => {
    setPreferenceState((current) => {
      const next: ThemePreference =
        resolveThemeMode(current, systemPrefersDark) === 'dark' ? 'light' : 'dark';
      persistThemePreference(next);
      return next;
    });
  }, [systemPrefersDark]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ preference, mode, setPreference, toggleMode }),
    [preference, mode, setPreference, toggleMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
